import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import type { ColumnMapping } from '@/hooks/useImportExport';

const TARGET_FIELDS = [
  'Task Name',
  'Start Date',
  'Finish Date',
  'Duration',
  '% Complete',
  'Predecessors',
  'WBS',
  'Status',
  'Priority',
  'Description',
] as const;

interface ColumnMapperProps {
  mappings: ColumnMapping[];
  onApply: (mappings: ColumnMapping[]) => void;
  onCancel: () => void;
}

function ConfidenceBar({ confidence }: { confidence: number }) {
  let colorClass = 'bg-red-500';
  if (confidence >= 80) {
    colorClass = 'bg-green-500';
  } else if (confidence >= 50) {
    colorClass = 'bg-yellow-500';
  }

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 rounded-full bg-muted">
        <div
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${Math.min(confidence, 100)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{confidence}%</span>
    </div>
  );
}

export function ColumnMapper({ mappings: initialMappings, onApply, onCancel }: ColumnMapperProps) {
  const [mappings, setMappings] = useState<ColumnMapping[]>(initialMappings);

  const handleTargetChange = (index: number, targetField: string) => {
    setMappings((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, targetField, confidence: 100 } : m
      )
    );
  };

  const handleApply = () => {
    onApply(mappings);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2 text-left font-medium">Source Column</th>
              <th className="px-4 py-2 text-left font-medium">Target Field</th>
              <th className="px-4 py-2 text-left font-medium">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((mapping, index) => (
              <tr key={mapping.sourceColumn} className="border-b last:border-b-0">
                <td className="px-4 py-2 font-mono text-xs">
                  {mapping.sourceColumn}
                </td>
                <td className="px-4 py-2">
                  <Select
                    value={mapping.targetField}
                    onValueChange={(value) => handleTargetChange(index, value)}
                  >
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TARGET_FIELDS.map((field) => (
                        <SelectItem key={field} value={field}>
                          {field}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2">
                  <ConfidenceBar confidence={mapping.confidence} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleApply}>Apply Mappings</Button>
      </div>
    </div>
  );
}
